import { InferProps } from "prop-types";
import React, { useCallback, useRef } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import CustomValue from "./custom_value";

type DraggableCustomValuesProps = Omit<InferProps<typeof CustomValue.propTypes>, "item" | "isDragged"> & {
  fields: Field[];
  setFields: (fields: Field[]) => void;
  updateOrder: (fields: Field[]) => void;
};

type Field = { id: number };

const DraggableCustomValues = ({ fields, updateOrder, setFields, ...props }: DraggableCustomValuesProps) => {
  const fieldsRef = useRef<HTMLDivElement>(null);
  const { isHierarchical, setMergeList } = props;

  const handleDragEnd = useCallback(
    (result) => {
      if (!result.destination) {
        return;
      }

      const newItems = [...fields];
      const [item] = newItems.splice(result.source.index, 1);
      newItems.splice(result.destination.index, 0, item);
      setFields(newItems);
      updateOrder(newItems);
    },
    [fields, updateOrder, setFields]
  );

  const handleDragStart = useCallback(
    () =>
      setMergeList((prev) => {
        const newList = prev.map(() => ({
          renderMerge: false
        }));
        return newList;
      }),
    [setMergeList]
  );

  return (
    <DragDropContext onBeforeCapture={handleDragStart} onDragEnd={handleDragEnd}>
      <div ref={fieldsRef}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <>
              {provided.placeholder}

              <div {...provided.droppableProps} ref={provided.innerRef}>
                {fields.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={`${item.id}`}
                    index={index}
                    isDragDisabled={!isHierarchical}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={provided.draggableProps.style}
                      >
                        <CustomValue
                          isDragged={snapshot.isDragging}
                          key={index}
                          index={index}
                          item={item}
                          {...props}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
              </div>
            </>
          )}
        </Droppable>
      </div>
    </DragDropContext>
  );
};

export default DraggableCustomValues;
